/**
 * 角色落地页工具 — 依据登录主体身份决定默认首页
 * 与 /login 共享同一判定规则，避免「根路径 / 分流」与「登录页默认跳转」两处逻辑分叉。
 *
 * 判定优先级（与 Login 页选择逻辑、后端 Claims 语义保持一致）：
 *   1. 司机：主体 identity==='driver'（N6 司机独立登录）或 roles 含 driver（旧版司机平台账号）→ /driver
 *   2. 企业用户：roles 含 enterprise → /enterprise
 *   3. 政府/监管及其他平台用户 → /gov（保持既有兜底）
 */
import { USER_KEY } from '@/api/http';
import type { User } from '@/types';

export const HOME_DRIVER = '/driver';
export const HOME_ENTERPRISE = '/enterprise';
export const HOME_GOV = '/gov';

/** 读取 localStorage 中持久化的登录主体；缺失/损坏返回 null */
export function readPrincipal(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as User;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** 是否为司机主体（司机独立登录 identity=driver，或旧版 roles 含 driver） */
export function isDriverPrincipal(principal: User | null | undefined): boolean {
  if (!principal) return false;
  return principal.identity === 'driver' || (Array.isArray(principal.roles) && principal.roles.includes('driver'));
}

/** 是否为企业用户主体（roles 含 enterprise） */
export function isEnterprisePrincipal(principal: User | null | undefined): boolean {
  if (!principal) return false;
  return Array.isArray(principal.roles) && principal.roles.includes('enterprise');
}

/** 按主体计算默认落地页（无可信主体时回落 /gov，与旧行为一致） */
export function homeForPrincipal(principal: User | null | undefined): string {
  if (isDriverPrincipal(principal)) return HOME_DRIVER;
  if (isEnterprisePrincipal(principal)) return HOME_ENTERPRISE;
  return HOME_GOV;
}

/** 读取当前 localStorage 登录主体并返回默认落地页 */
export function defaultHome(): string {
  return homeForPrincipal(readPrincipal());
}
